import { useEffect, useRef, useState } from "react";

/**
 * usePrefersReducedMotion — tracks the OS "reduce motion" setting.
 *
 * Returns false when matchMedia is unavailable (jsdom), so tests exercise the
 * animated path by default.
 */
export function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia?.("(prefers-reduced-motion: reduce)");
    if (!mq) return undefined;
    setReduced(mq.matches);
    const onChange = (e) => setReduced(e.matches);
    // Safari < 14 only has the deprecated addListener.
    if (mq.addEventListener) {
      mq.addEventListener("change", onChange);
      return () => mq.removeEventListener("change", onChange);
    }
    mq.addListener(onChange);
    return () => mq.removeListener(onChange);
  }, []);

  return reduced;
}

const FOCUSABLE = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(", ");

/**
 * useFocusTrap — moves focus into `panelRef` while `active`, keeps Tab inside it,
 * and restores focus to whatever was focused before on deactivation.
 *
 * The trap yields when the active element sits outside the panel: dropdowns and
 * the unsaved-changes Modal render in their own portals, and stealing focus back
 * from them would break those controls.
 *
 * `trapTab` gates ONLY the Tab cycling, deliberately separate from `active`.
 * When the unsaved-changes confirm opens, focus is still inside the panel (on
 * the close button that opened it), so the "active element is outside" escape
 * hatch above does not fire and Tab would cycle the drawer forever, leaving
 * Cancel/Discard unreachable. Passing `active: false` instead would run the
 * first effect's cleanup and restore focus outside the drawer entirely — hence
 * two flags rather than one.
 */
export function useFocusTrap(panelRef, active, trapTab = true) {
  const previouslyFocused = useRef(null);

  useEffect(() => {
    if (!active) return undefined;
    previouslyFocused.current = document.activeElement;
    const panel = panelRef.current;
    const first = panel?.querySelector(FOCUSABLE);
    (first || panel)?.focus();

    return () => {
      const el = previouslyFocused.current;
      if (el && typeof el.focus === "function" && document.contains(el)) {
        el.focus();
      }
    };
  }, [active, panelRef]);

  useEffect(() => {
    if (!active || !trapTab) return undefined;
    const onKeyDown = (e) => {
      if (e.key !== "Tab") return;
      const panel = panelRef.current;
      if (!panel) return;
      // Another portal (dropdown, confirm modal) owns focus — leave it alone.
      if (!panel.contains(document.activeElement)) return;
      const items = Array.from(panel.querySelectorAll(FOCUSABLE));
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [active, trapTab, panelRef]);
}
